import { useLazyQuery } from '@apollo/client';
import { Frequency, type WorkflowCompletion } from '@generated/graphql';
import InfoDialog from '@ovComponents/2-component/infoDialog/infoDialog';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { additionalInfo } from '@ovComponents/4-module/configurableOptionFields/additionalInfo';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { DEPOSIT_TRANSFER_TYPE } from '@ovComponents/4-module/workflowCompletion/constants/deposits';
import { FISCAL_YEAR_SELECT_TEST_ID } from '@ovComponents/4-module/workflowCompletion/constants/testIds';
import {
  type ValidateTransferResponse,
  validateTransfer,
} from '@ovComponents/accountRestrictions';
import { TransferErrorBanner } from '@ovComponents/transferErrorBanner';
import { UserContext } from '@providers/userContextProvider';
import { DEFAULT_SUPPORT_URL } from '@utils/constants';
import dayjs from 'dayjs';
import { Alert, Typography, useTheme, useToast } from 'hephaestus/web';
import { isNull, isUndefined } from 'lodash-es';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { useParams } from 'react-router';
import remarkGfm from 'remark-gfm';
import { useLocalization } from '../../../../../hooks/useLocalization';
import { usePermission } from '../../../../../hooks/usePermission';
import { FETCH_TOTAL_TRANSFERS } from '../queries';
import { invalidFields } from '../utils';
import {
  getDepositErrorBannerKey,
  isFhsaTransferFormEnabled,
  isFhsaTransferRuleApplicable,
} from '../utils/depositAccountRestrictions';
import { getDepositValidationError } from '../utils/depositAmountRules';
import { buildDepositFieldOptions } from '../utils/depositFieldOptions';
import {
  depositAccountConfig,
  showFiscalYearBasedOnAccountType,
} from '../utils/depositFiscalYear';
import { DepositStarting } from '../utils/depositSchedule';
import { useDepositSchedule } from '../utils/useDepositSchedule';
import { useDepositWorkflowData } from '../utils/useDepositWorkflowData';
import { DepositEft } from './eft/DepositEft';
import { useDepositEft } from './eft/useDepositEft';
import { DepositExternalTransfer } from './externalTransfer/DepositExternalTransfer';
import { DepositExternalTransferTypeAlerts } from './externalTransfer/DepositExternalTransferTypeAlerts';
import { useDepositExternalTransfer } from './externalTransfer/useDepositExternalTransfer';
import { DepositInternalTransfer } from './internalTransfer/DepositInternalTransfer';
import { useDepositInternalTransfer } from './internalTransfer/useDepositInternalTransfer';

export const DepositForm = ({
  options,
  onNext,
  dummyDeposit,
  setDummyDeposit,
  workflowCompletion,
}: {
  options: any;
  onNext: any;
  dummyDeposit?: any;
  setDummyDeposit?: any;
  stepLoading?: boolean;
  workflowCompletion?: WorkflowCompletion;
}) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation('workflowCompletions');
  const theme = useTheme();
  const { userId: paramsUserId } = useParams();
  const canWriteOverrideTransferRestrictions = usePermission(
    'write:override_transfer_restrictions',
  );
  const { enqueueToast } = useToast();
  const { activeEntity, activeOrganization, userContext } =
    useContext(UserContext);

  const {
    applicableData,
    updateContextWithFields,
    saveTemporaryDeposit,
    mode,
  } = useDepositWorkflowData(dummyDeposit, setDummyDeposit, workflowCompletion);

  // Seed `applicableData.type` from `options.defaultTransferType` on entry
  // when DEPOSIT_TYPE is not in the workflow. The `applicableData?.type`
  // guard makes this a no-op when DEPOSIT_TYPE precedes the form (type
  // already set) or when editing an existing deposit (type hydrated from
  // the deposit).
  useEffect(() => {
    if (mode === 'edit') return;
    if (applicableData?.type) return;
    const fallback = options?.defaultTransferType;
    if (!fallback) return;
    updateContextWithFields({ type: fallback });
  }, []);

  const [focused, setFocused] = useState<string[]>([]);
  const [disableForm, setDisableForm] = useState<boolean>(false);
  const [totalTransfers, setTotalTransfers] = useState<number | null>(null);
  const [validateErrorAmounts, setValidateErrorAmounts] = useState({
    hasError: false,
    errorMessage: '',
  });
  const [scheduleError, setScheduleError] = useState<string>('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Prefer sub-account owner over activeEntity when the route has no :userId (household nav).
  const userIdFromSubAccount = applicableData?.subAccount?.account?.user?.id;
  const userId = paramsUserId ?? userIdFromSubAccount ?? activeEntity?.id;

  const overrideTransferRestrictions = canWriteOverrideTransferRestrictions;
  const externalTransferForm = useDepositExternalTransfer({
    deposit: applicableData,
  });
  const [fetchTotal] = useLazyQuery(FETCH_TOTAL_TRANSFERS);

  const scheduledDateForConfig = applicableData?.scheduledDate;
  const applicableAccountType = applicableData?.subAccount?.account
    ?.type as keyof ReturnType<typeof depositAccountConfig>;

  const depositConfig = depositAccountConfig(scheduledDateForConfig)[
    applicableAccountType
  ];
  const isCurrentContributionYearEnabled = depositConfig
    ? dayjs().isAfter(depositConfig.endOfPriorFiscalYear)
    : false;

  const generateContributionYearMenuItems = () => {
    const currentScheduleDate = applicableData?.scheduledDate;
    if (
      applicableData.schedule === Frequency.OneTime &&
      starting === DepositStarting.Now
    ) {
      const isCurrentFiscalYearEnabled = depositConfig
        ? dayjs().isAfter(depositConfig.endOfPriorFiscalYear)
        : false;
      const isPreviousFiscalYearEnabled = depositConfig
        ? dayjs().isBefore(depositConfig.endOfPriorFiscalYear) ||
          dayjs().isSame(depositConfig.endOfPriorFiscalYear)
        : false;
      return [
        isPreviousFiscalYearEnabled && (
          <MenuItem
            data-testid={FISCAL_YEAR_SELECT_TEST_ID.LAST_YEAR}
            value={currentYear - 1}
          >
            {currentYear - 1}
          </MenuItem>
        ),
        (isCurrentFiscalYearEnabled || isPreviousFiscalYearEnabled) && (
          <MenuItem
            data-testid={FISCAL_YEAR_SELECT_TEST_ID.CURRENT_YEAR}
            value={currentYear}
          >
            {currentYear}
          </MenuItem>
        ),
      ];
    }
    if (
      applicableData.schedule === Frequency.OneTime &&
      starting === DepositStarting.FutureDate
    ) {
      const currentSelectedYear = dayjs(currentScheduleDate).year();
      const isCurrentFiscalYearEnabled = depositConfig
        ? dayjs(currentScheduleDate).isAfter(depositConfig.endOfPriorFiscalYear)
        : false;
      const isPreviousFiscalYearEnabled = depositConfig
        ? dayjs(currentScheduleDate).isBefore(
            depositConfig.endOfPriorFiscalYear,
          ) ||
          dayjs(currentScheduleDate).isSame(depositConfig.endOfPriorFiscalYear)
        : false;
      return [
        isPreviousFiscalYearEnabled && (
          <MenuItem
            data-testid={FISCAL_YEAR_SELECT_TEST_ID.LAST_YEAR}
            value={currentSelectedYear - 1}
          >
            {currentSelectedYear - 1}
          </MenuItem>
        ),
        (isCurrentFiscalYearEnabled || isPreviousFiscalYearEnabled) && (
          <MenuItem
            data-testid={FISCAL_YEAR_SELECT_TEST_ID.CURRENT_YEAR}
            value={currentSelectedYear}
          >
            {currentSelectedYear}
          </MenuItem>
        ),
      ];
    }
    if (applicableData.schedule !== Frequency.OneTime) {
      const currentSelectedYear = dayjs(currentScheduleDate).year();
      return [
        currentSelectedYear && (
          <MenuItem
            data-testid={FISCAL_YEAR_SELECT_TEST_ID.CURRENT_YEAR}
            value={currentSelectedYear}
          >
            {currentSelectedYear}
          </MenuItem>
        ),
      ];
    }

    return [];
  };

  const hyperLinkColor = theme.color.negativeOutline;
  const supportUrl =
    translateBackendString(userContext.organization?.translatedSupportUrl) ||
    DEFAULT_SUPPORT_URL;
  const currentYear = dayjs().year();

  const clearValidateErrorAmountsState = () =>
    setValidateErrorAmounts({ hasError: false, errorMessage: '' });

  const handleTotalTransfers = async (subAccount: { id: string }) => {
    const { data } = await fetchTotal({
      variables: { input: { filter: { subAccountId: subAccount.id } } },
    });
    setTotalTransfers(data.fetchTransfers?.totalCount);
    checkValidationErrors(data.fetchTransfers?.totalCount);
  };

  const depositSchedule = useDepositSchedule({
    options,
    deposit: applicableData,
    updateContextWithFields,
    userId,
  });
  const { starting, validTransferDelayCheck } = depositSchedule;
  const eftTransferForm = useDepositEft({
    deposit: applicableData,
    userId,
    workflowCompletion,
    schedule: depositSchedule,
  });

  const checkValidationErrors = (transfersCount: number | null) => {
    clearValidateErrorAmountsState();
    setScheduleError('');

    const validationError = getDepositValidationError({
      applicableData,
      options,
      transfersCount,
      organization: activeOrganization,
      t,
    });

    if (validationError?.field === 'amountCents') {
      setValidateErrorAmounts({
        hasError: true,
        errorMessage: validationError.message,
      });
    }
    if (validationError?.field === 'schedule') {
      setScheduleError(validationError.message);
    }
  };

  useEffect(() => {
    if (!isUndefined(applicableData?.amountCents)) {
      checkValidationErrors(totalTransfers);
    }
  }, [applicableData?.amountCents, applicableData?.schedule]);

  useEffect(() => {
    const update = async () => {
      if (applicableData?.subAccount?.id) {
        await handleTotalTransfers({
          id: applicableData.subAccount.id,
          ...applicableData.subAccount,
        });
      }
    };
    update();
  }, [applicableData?.subAccount]);

  const disabled = disableForm;

  const handleValue = (value: Partial<typeof applicableData>) =>
    updateContextWithFields(value);
  const focus = (field: string) => setFocused([...focused, field]);
  const setFormEnabled = (enabled: boolean) => setDisableForm(!enabled);

  useEffect(() => {
    if (applicableData?.schedule !== Frequency.OneTime) {
      handleValue({ fiscalYear: undefined });
    }
    if (isCurrentContributionYearEnabled) {
      handleValue({ fiscalYear: currentYear });
    }
  }, [applicableData?.schedule, starting, options]);

  useEffect(() => {
    if (!isFhsaTransferRuleApplicable({ applicableData })) return;
    const isFormEnabled = isFhsaTransferFormEnabled({
      applicableData,
      overrideTransferRestrictions,
    });
    setDisableForm(!isFormEnabled);
  }, [applicableData]);

  const internalTransferForm = useDepositInternalTransfer({
    deposit: applicableData,
    updateContextWithFields,
  });

  const submit = async () => {
    setIsSubmitted(true);

    // If no deposit type is selected, allow user to proceed without saving data
    if (!applicableData?.type) {
      enqueueToast(t('noDepositTypeSelectedWarning'), {
        variant: 'warning',
      });
      await onNext();
      return;
    }

    const fields = invalidFields(
      buildDepositFieldOptions({
        options,
        applicableData,
        starting,
        participantNumberEnabled: externalTransferForm.participantNumberEnabled,
        isMultiCurrencyInternalTransfer:
          internalTransferForm.isMultiCurrencyInternal,
        hasAvailableCurrencies:
          internalTransferForm.availableCurrencies.length > 0,
      }),
      applicableData,
    );

    if (isNull(totalTransfers) && applicableData?.subAccount?.id) {
      await handleTotalTransfers({
        id: applicableData.subAccount.id,
        ...applicableData.subAccount,
      });
    }
    if (!isUndefined(applicableData?.amountCents)) {
      checkValidationErrors(totalTransfers);
    }
    if (validateErrorAmounts.hasError) {
      fields.push('amountCents');
    }

    if (externalTransferForm.hasInvalidParticipantNumber) {
      fields.push('participantNumber');
    }

    if (internalTransferForm.checkInsufficientBalance()) {
      fields.push('amountCents');
    }

    if (!validTransferDelayCheck) {
      fields.push('scheduledDate');
    }

    if (fields.length > 0) {
      setFocused([...fields]);
      return;
    }
    const transferValidation: ValidateTransferResponse = validateTransfer({
      accountTo: applicableData?.subAccount?.account?.type ?? '',
      transferType:
        applicableData?.type === DEPOSIT_TRANSFER_TYPE.EFT
          ? 'electronicFundsTransferIn'
          : applicableData?.type === DEPOSIT_TRANSFER_TYPE.EXTERNAL_TRANSFER
            ? 'externalTransfer'
            : 'internalTransferTo',
      accountFrom: applicableData?.fromSubAccount?.account?.type,
      canOverrideTransferRestrictions: canWriteOverrideTransferRestrictions,
    });

    if (!transferValidation.isValid) {
      enqueueToast(transferValidation.message, { variant: 'error' });
      return;
    }
    if (fields.length === 0) {
      await saveTemporaryDeposit();
      await onNext();
    }
  };

  const renderTransferFields = () => {
    switch (applicableData?.type) {
      case DEPOSIT_TRANSFER_TYPE.EFT:
        return (
          <DepositEft
            deposit={applicableData}
            options={options}
            focused={focused}
            onFocus={focus}
            onChange={handleValue}
            amountError={validateErrorAmounts}
            userId={userId}
            transfer={eftTransferForm}
            schedule={depositSchedule}
            scheduleError={scheduleError}
            totalTransfers={totalTransfers}
            refreshTotalTransfers={handleTotalTransfers}
            onFormEnabledChange={setFormEnabled}
            fiscalYearMenuItems={generateContributionYearMenuItems()}
            isSubmitted={isSubmitted}
          />
        );
      case DEPOSIT_TRANSFER_TYPE.INTERNAL_TRANSFER:
        return (
          <DepositInternalTransfer
            deposit={applicableData}
            options={options}
            focused={focused}
            onFocus={focus}
            onChange={handleValue}
            amountError={validateErrorAmounts}
            userId={userId}
            workflowCompletion={workflowCompletion}
            transfer={internalTransferForm}
            onFormEnabledChange={setFormEnabled}
            fiscalYearMenuItems={generateContributionYearMenuItems()}
          />
        );
      case DEPOSIT_TRANSFER_TYPE.EXTERNAL_TRANSFER:
        return (
          <DepositExternalTransfer
            deposit={applicableData}
            options={options}
            focused={focused}
            onFocus={focus}
            onChange={handleValue}
            amountError={validateErrorAmounts}
            userId={userId}
            workflowCompletion={workflowCompletion}
            transfer={externalTransferForm}
          />
        );
      default:
        return null;
    }
  };

  const getErrorBanner = () => {
    const i18nKey = getDepositErrorBannerKey({
      applicableData,
      overrideTransferRestrictions,
    });

    if (!i18nKey) return null;

    return (
      <TransferErrorBanner
        showHeader={false}
        theme={theme}
        i18nKey={i18nKey}
        supportUrl={supportUrl}
        linkSx={{ color: hyperLinkColor }}
      />
    );
  };

  return (
    <WorkflowCompletionForm
      onSubmit={submit}
      submitButtonProps={{
        label: t('continue'),
        disabled,
        dataTestId: 'deposit-form-continue-button',
      }}
    >
      <Typography
        variant='displayLarge'
        sx={{ mt: 1, mb: options?.subtitle ? 0 : 2 }}
      >
        {translateBackendString(options?.title)}
      </Typography>
      {options?.subtitle && (
        <Typography variant='bodyLarge' component='div' sx={{ mb: 3 }}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} linkTarget={'_blank'}>
            {translateBackendString(options?.subtitle)}
          </ReactMarkdown>
          {additionalInfo(
            translateBackendString,
            options?.subtitle?.additionalInfo,
          ) && (
            <InfoDialog
              information={
                additionalInfo(
                  translateBackendString,
                  options?.subtitle?.additionalInfo,
                ) ?? ''
              }
            />
          )}
        </Typography>
      )}
      {!applicableData?.type && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          {t('noDepositTypeSelected')}
        </Alert>
      )}
      {renderTransferFields()}

      {options?.transferDelay?.enabled &&
        ((applicableData.schedule === Frequency.OneTime &&
          starting !== DepositStarting.Now) ||
          applicableData.schedule !== Frequency.OneTime) && (
          <Alert severity='info' data-testid='transfer-delay-alert'>
            {translateBackendString(options?.transferDelayDescription)}
          </Alert>
        )}

      {applicableData?.schedule !== Frequency.OneTime &&
        showFiscalYearBasedOnAccountType(
          applicableData?.subAccount?.account?.type,
        ) && (
          <Alert severity='info' data-testid='transfer-delay-alert'>
            <Typography variant='bodySmall' component='p'>
              {t('fiscalYearForRecurringContributions')}
            </Typography>
          </Alert>
        )}

      {/* Later this section will be implement as editable from configuration level */}
      {applicableData?.transferType &&
        applicableData?.type === DEPOSIT_TRANSFER_TYPE.EXTERNAL_TRANSFER && (
          <DepositExternalTransferTypeAlerts
            transferType={applicableData.transferType}
          />
        )}

      {getErrorBanner()}
    </WorkflowCompletionForm>
  );
};

export default DepositForm;
